'use client';

import type { ComponentType } from 'react';

import { AdyenDataCollectionV2 } from './adyen/AdyenDataCollection.v2';
import { AfterPayV2 } from './after-pay/AfterPay.v2';
import { BazaarVoiceV2 } from './bazaarvoice/BazaarVoice.v2';
import { BazaarVoiceContextualV2 } from './bazaarvoice/contextual-script/BazaarVoiceContextual.v2';
import { BranchV2 } from './branch/Branch.v2';
import { GtmV2 } from './google/gtm/GTM.v2';
import { MedalliaV2 } from './medallia/Medallia.v2';
import { OneTrustV2 } from './one-trust/OneTrust.v2';
import { OptimizelyV2 } from './optimizely/Optimizely.v2';
import { UsableNetV2 } from './usablenet/components/UsableNet.v2';

type ScriptEntry = { id: string; Component: ComponentType };
type ScriptsPageType = 'hcp' | 'plp' | 'pdp' | 'cart' | 'checkout' | 'acp' | 'aos';

const SCRIPT_CATALOG = {
    'usable-net': UsableNetV2,
    'one-trust': OneTrustV2,
    gtm: GtmV2,
    optimizely: OptimizelyV2,
    medallia: MedalliaV2,
    adyen: AdyenDataCollectionV2,
    'after-pay': AfterPayV2,
    branch: BranchV2,
    'bazaarvoice-contextual': BazaarVoiceContextualV2,
    bazaarvoice: BazaarVoiceV2,
} satisfies Record<string, ComponentType>;

type ScriptId = keyof typeof SCRIPT_CATALOG;

type ScriptIdList = readonly ScriptId[];

function buildScripts(ids: ScriptIdList): ScriptEntry[] {
    return ids.map(id => ({ id, Component: SCRIPT_CATALOG[id] }));
}

const DEFAULT_SCRIPT_IDS = ['usable-net', 'gtm', 'optimizely'] as const satisfies ScriptIdList;

const COMMERCE_SCRIPT_IDS = [
    'one-trust',
    'adyen',
    'branch',
    'medallia',
    'after-pay',
    'bazaarvoice-contextual',
] as const satisfies ScriptIdList;

const SCRIPTS_BY_PAGE_TYPE_IDS: Partial<Record<ScriptsPageType, ScriptIdList>> = {
    hcp: COMMERCE_SCRIPT_IDS,
    plp: COMMERCE_SCRIPT_IDS,
    pdp: [...COMMERCE_SCRIPT_IDS, 'bazaarvoice'] as const satisfies ScriptIdList,
    cart: COMMERCE_SCRIPT_IDS,
    checkout: COMMERCE_SCRIPT_IDS,
    acp: ['one-trust', 'adyen', 'after-pay', 'medallia'] as const satisfies ScriptIdList,
    aos: [] as const satisfies ScriptIdList,
};

function isScriptsPageType(value: string): value is ScriptsPageType {
    return value in SCRIPTS_BY_PAGE_TYPE_IDS;
}

function dedupeScriptEntries(entries: ScriptEntry[]): ScriptEntry[] {
    const seen = new Set<string>();

    return entries.filter(({ id }) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
    });
}

export function resolveScripts(pageType: string): ScriptEntry[] {
    const pageIds = isScriptsPageType(pageType) ? (SCRIPTS_BY_PAGE_TYPE_IDS[pageType] ?? []) : [];
    return dedupeScriptEntries(buildScripts([...DEFAULT_SCRIPT_IDS, ...pageIds]));
}
